import { useState } from 'react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { Download, MailOpen, Reply, X } from 'lucide-react';
import { useMoveGraftedEmail, useRemoveGraftedEmail } from '../../hooks/queries/useDossierGraftEmails';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { Button } from '../ui/Button';
import { downloadEmailAsEml, type MergedEmail } from '../../services/dossiers/dossierEmailService';
import { buildReplyMailto } from '../../services/dossiers/emailReply';
import { canOpenInOutlook, openGraftInOutlook } from '../../services/dossiers/outlookOpen';

const DIRECTION_LABEL: Record<string, string> = {
  inbound_forward: 'transfert entrant',
  outbound_bcc: "copie d'envoi (BCC)",
  outbound_addin: 'envoyé depuis Outlook (add-in)',
  inbound_addin: 'greffé depuis Outlook (add-in)',
};

function sanitizeHtml(html: string | null): string | null {
  return html
    ? String(DOMPurify.sanitize(html, {
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
        FORBID_ATTR: ['onerror', 'onclick', 'onload'],
      }))
    : null;
}

export function DossierEmailViewerModal({
  email,
  dossierId,
  onClose,
}: {
  email: MergedEmail;
  dossierId: string;
  onClose: () => void;
}) {
  const removeGraftedEmail = useRemoveGraftedEmail();
  const moveGrafted = useMoveGraftedEmail();
  const { data: dossiers } = useDossiers();
  const [moveOpen, setMoveOpen] = useState(false);
  const [targetId, setTargetId] = useState('');

  const isGmail = email.kind === 'gmail';
  const fromName = isGmail ? (email.row.from_name ?? email.row.from_email) : email.row.from_email;
  const fromEmail = email.row.from_email;
  const subject = email.row.subject ?? '(sans objet)';
  const sanitized = sanitizeHtml(email.row.body_html);

  const handleRemove = () => {
    if (email.kind !== 'graft') return;
    if (!window.confirm('Retirer ce mail du dossier ? Les pièces jointes rattachées seront supprimées.')) return;
    removeGraftedEmail.mutate(
      { emailId: email.row.id, dossierId },
      {
        onSuccess: () => {
          toast.success('Mail retiré du dossier');
          onClose();
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Retrait impossible');
        },
      },
    );
  };

  const handleMove = () => {
    if (email.kind !== 'graft' || !targetId) return;
    moveGrafted.mutate(
      { emailId: email.row.id, dossierId, targetDossierId: targetId },
      {
        onSuccess: (data) => {
          const skipped = Array.isArray((data as { files_skipped?: unknown[] })?.files_skipped)
            ? (data as { files_skipped: unknown[] }).files_skipped.length
            : 0;
          if (skipped > 0) {
            toast.warning(`Mail déplacé, mais ${skipped} pièce(s) jointe(s) n'ont pas pu être déplacées.`);
          } else {
            toast.success('Mail déplacé, pièces jointes comprises.');
          }
          onClose();
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Déplacement impossible'),
      },
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <div className="font-medium">{fromName}</div>
            <div className="text-xs text-gray-500">{fromEmail}</div>
            <div className="text-sm mt-1">{subject}</div>
            {email.kind === 'graft' && (
              <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                <div>
                  Sens :{' '}
                  {DIRECTION_LABEL[email.row.direction] ?? email.row.direction}
                </div>
                {email.row.to_emails.length > 0 && <div>À : {email.row.to_emails.join(', ')}</div>}
                {email.row.cc_emails.length > 0 && <div>Cc : {email.row.cc_emails.join(', ')}</div>}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-4 flex-1">
          {sanitized ? (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: sanitized }} />
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-sm">{email.row.body_text}</pre>
          )}
        </div>
        <div className="p-3 border-t flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-gray-500">
            Envoyé le {new Date(email.sentAt).toLocaleString('fr-FR')}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {canOpenInOutlook(email) ? (
              <Button
                variant="primary"
                size="sm"
                title="Ouvre le message original dans Outlook"
                onClick={() => openGraftInOutlook(email.row)}
              >
                <MailOpen className="w-4 h-4" />
                Ouvrir dans Outlook
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                title="Télécharge une copie .eml (pas d’identifiant Outlook)"
                onClick={() => {
                  toast.promise(downloadEmailAsEml(email, dossierId), {
                    loading: 'Préparation du fichier…',
                    success: 'Fichier téléchargé — ouvrez-le pour répondre dans Outlook.',
                    error: 'Impossible de générer le fichier.',
                  });
                }}
              >
                <Download className="w-4 h-4" />
                Télécharger .eml
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              title="Réponse rapide (texte brut, historique tronqué)"
              onClick={() => { window.location.href = buildReplyMailto(email.row); }}
            >
              <Reply className="w-4 h-4" />
              Réponse rapide
            </Button>
            {email.kind === 'graft' && !moveOpen && (
              <Button variant="secondary" size="sm" onClick={() => setMoveOpen(true)}>
                Déplacer vers un autre dossier
              </Button>
            )}
            {email.kind === 'graft' && moveOpen && (
              <div className="flex items-center gap-2">
                <select
                  className="rounded border border-neutral-200 px-2 py-1 text-xs text-neutral-700 max-w-72"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                >
                  <option value="">Dossier de destination…</option>
                  {(dossiers ?? []).filter((d) => d.id !== dossierId).map((d) => (
                    <option key={d.id} value={d.id}>{d.number} — {d.title}</option>
                  ))}
                </select>
                <Button variant="primary" size="sm" disabled={!targetId} isLoading={moveGrafted.isPending}
                  onClick={handleMove}>
                  Déplacer
                </Button>
              </div>
            )}
            {email.kind === 'graft' && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleRemove}
                isLoading={removeGraftedEmail.isPending}
              >
                Retirer du dossier
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
